import React, { useState } from 'react';
import { useAuthStore } from '../stores';
import { API_URL } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
    Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, 
    Loader2, Save, Calendar as CalendarIcon 
} from 'lucide-react';

// --- IMPORTS POUR LE CALENDRIER ---
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "../lib/utils"; // Assurez-vous d'avoir votre utilitaire cn
import { Calendar } from "../components/ui/calendar"; // Votre composant Calendar Shadcn
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";

// --- COMPOSANT DATE PICKER RÉUTILISABLE ---
const DatePicker = ({ date, setDate, placeholder = "Choisir une date" }) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "d MMMM yyyy", { locale: fr }) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={setDate}
          initialFocus
          locale={fr}
        />
      </PopoverContent>
    </Popover>
  );
};

const ENTITIES = [
  { value: 'clients', label: 'Clients' },
  { value: 'leads', label: 'Prospects (Leads)' },
  { value: 'tours', label: 'Tournées' },
  { value: 'products', label: 'Produits' }
];

const STATUS_OPTIONS = {
    leads: [
        {value: 'all', label: 'Tous les statuts'},
        {value: 'new', label: 'Nouveaux'},
        {value: 'won', label: 'Gagnés'},
        {value: 'lost', label: 'Perdus'},
        {value: 'unclassified', label: 'Non classés'}
    ],
    tours: [
        {value: 'all', label: 'Tous les statuts'},
        {value: 'planned', label: 'Planifiée'},
        {value: 'completed', label: 'Terminée'},
        {value: 'cancelled', label: 'Annulée'}
    ],
    clients: [
        {value: 'all', label: 'Tous'}
    ],
    products: [
        {value: 'all', label: 'Tous'}
    ]
};

const DataManagementPage = () => {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(false);
  
  // --- ÉTATS IMPORT ---
  const [importType, setImportType] = useState('clients');
  const [importMode, setImportMode] = useState('append'); 
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [password, setPassword] = useState(''); 

  // --- ÉTATS EXPORT ---
  const [exportType, setExportType] = useState('leads');
  const [exportFormat, setExportFormat] = useState('xlsx');
  
  // MODIFICATION ICI : On stocke des objets Date (ou null) au lieu de strings vides
  const [dateRange, setDateRange] = useState({ start: undefined, end: undefined });
  
  const [exportStatus, setExportStatus] = useState('all');

  // --- HANDLERS IMPORT (Inchangés) ---
  const downloadTemplate = async () => { window.open(`${API_URL}/data/template/${importType}`, '_blank'); };
  
  const handleFileUpload = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    setLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const res = await fetch(`${API_URL}/data/import/preview/${importType}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if(data.columns) { data.columns = data.columns.map(c => c.replace('*', '').trim()); }
      setPreviewData(data);
    } catch (err) { toast.error("Erreur lecture fichier"); } finally { setLoading(false); }
  };

  const commitImport = async () => {
    if (importMode === 'replace' && !password) { toast.error("Mot de passe requis"); return; }
    if (!window.confirm("Confirmer l'import ?")) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/data/import/commit/${importType}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ data: previewData.data, mode: importMode, password: password })
      });
      const result = await res.json();
      if(result.status === 'success') {
          toast.success(`${result.imported} éléments importés !`);
          setPreviewData(null); setFile(null); setPassword('');
      } else { toast.error("Erreur import"); }
    } catch (err) { toast.error("Erreur serveur"); } finally { setLoading(false); }
  };

  // --- HANDLER EXPORT MODIFIÉ (Formatage des Dates) ---
  const handleExport = async () => {
      setLoading(true);
      try {
          // MODIFICATION : Conversion des objets Date en string YYYY-MM-DD pour le backend
          const formatDate = (d) => d ? format(d, 'yyyy-MM-dd') : null;

          const filters = {
              format: exportFormat,
              status: exportStatus,
              start_date: formatDate(dateRange.start),
              end_date: formatDate(dateRange.end)
          };

          const response = await fetch(`${API_URL}/data/export/${exportType}`, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`
              },
              body: JSON.stringify(filters)
          });

          if (!response.ok) throw new Error("Erreur export");

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const contentDisposition = response.headers.get('Content-Disposition');
          let fileName = `export_${exportType}.xlsx`;
          if (contentDisposition) {
              const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
              if (fileNameMatch && fileNameMatch.length === 2) fileName = fileNameMatch[1];
          }
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          toast.success("Export téléchargé !");

      } catch (e) {
          console.error(e);
          toast.error("Impossible de générer l'export");
      } finally {
          setLoading(false);
      }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-heading">Gestion des Données</h1>
        <p className="text-muted-foreground">Importez ou exportez vos bases de données en masse.</p>
      </div>

      <Tabs defaultValue="import" className="space-y-4">
        <TabsList>
          <TabsTrigger value="import" className="gap-2"><Upload className="w-4 h-4"/> Import (Excel)</TabsTrigger>
          <TabsTrigger value="export" className="gap-2"><Download className="w-4 h-4"/> Export</TabsTrigger>
        </TabsList>

        {/* --- ONGLET IMPORT (Inchangé visuellement) --- */}
        <TabsContent value="import">
          <Card>
            <CardHeader>
              <CardTitle>Importer des données</CardTitle>
              <CardDescription>Ajoutez ou mettez à jour vos données via un fichier Excel/CSV.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Type de donnée</Label>
                  <Select value={importType} onValueChange={setImportType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ENTITIES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Mode d'import</Label>
                  <Select value={importMode} onValueChange={setImportMode}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="append">Ajouter (Conserver doublons)</SelectItem>
                      <SelectItem value="update">Mettre à jour (Basé sur Email/Ref)</SelectItem>
                      <SelectItem value="replace" className="text-red-600 font-bold">Remplacer tout (Danger)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end pb-1">
                   <Button variant="outline" onClick={downloadTemplate} className="w-full border-dashed">
                     <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600"/> Télécharger modèle
                   </Button>
                </div>
              </div>

              {importMode === 'replace' && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Zone de Danger</AlertTitle>
                  <AlertDescription className="space-y-2">
                    <p>Cette action effacera <strong>toutes</strong> les données de type "{importType}" avant d'importer le fichier.</p>
                    <Input 
                        type="password" 
                        placeholder="Mot de passe Administrateur requis" 
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="bg-white text-black max-w-sm"
                    />
                  </AlertDescription>
                </Alert>
              )}

              {!previewData && (
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-10 text-center hover:bg-slate-50 transition-colors">
                    <Input 
                        type="file" 
                        accept=".xlsx, .csv" 
                        onChange={handleFileUpload} 
                        className="hidden" 
                        id="file-upload" 
                        disabled={loading}
                    />
                    <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center gap-2">
                        {loading ? <Loader2 className="w-10 h-10 animate-spin text-primary"/> : <Upload className="w-10 h-10 text-slate-400"/>}
                        <span className="text-sm font-medium text-slate-400 dark:text-slate-500">Cliquez pour choisir un fichier Excel ou CSV</span>
                    </label>
                  </div>
              )}

              {previewData && (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4">
                    <div className="flex items-center justify-between">
                        <h3 className="font-bold flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5"/> 
                            Prévisualisation ({previewData.total} lignes)
                        </h3>
                        <div className="flex gap-2">
                            <Button variant="ghost" onClick={() => { setPreviewData(null); setFile(null); }}>Annuler</Button>
                            <Button onClick={commitImport} disabled={loading}>
                                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin"/>}
                                <Save className="w-4 h-4 mr-2"/> Confirmer l'import
                            </Button>
                        </div>
                    </div>
                    <div className="border rounded-md overflow-hidden max-h-[400px] overflow-y-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[50px]">État</TableHead>
                                    {previewData.columns.map(col => <TableHead key={col}>{col}</TableHead>)}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {previewData.data.slice(0, 50).map((row, i) => (
                                    <TableRow key={i} className={row._status === 'error' ? 'bg-red-50' : ''}>
                                        <TableCell>
                                            {row._status === 'error' ? 
                                                <AlertTriangle className="w-4 h-4 text-red-500" title={row._error}/> : 
                                                <CheckCircle2 className="w-4 h-4 text-green-500"/>
                                            }
                                        </TableCell>
                                        {previewData.columns.map(col => (
                                            <TableCell key={col} className="text-xs truncate max-w-[150px]">{JSON.stringify(row[col])}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- ONGLET EXPORT MIS À JOUR AVEC LE CALENDRIER --- */}
        <TabsContent value="export">
             <Card>
                <CardHeader>
                    <CardTitle>Exporter des données</CardTitle>
                    <CardDescription>Téléchargez vos données pour analyse externe.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Colonne Gauche */}
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Données à exporter</Label>
                                <Select value={exportType} onValueChange={(v) => { setExportType(v); setExportStatus('all'); }}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {ENTITIES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Format</Label>
                                <div className="flex gap-4">
                                    <div 
                                        onClick={() => setExportFormat('xlsx')}
                                        className={`cursor-pointer flex-1 border rounded-lg p-3 flex items-center gap-2 hover:bg-slate-50 ${exportFormat === 'xlsx' ? 'border-primary bg-primary/5 ring-1 ring-primary' : ''}`}
                                    >
                                        <FileSpreadsheet className="w-5 h-5 text-green-600"/>
                                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Excel (.xlsx)</div>
                                    </div>
                                    <div 
                                        onClick={() => setExportFormat('csv')}
                                        className={`cursor-pointer flex-1 border rounded-lg p-3 flex items-center gap-2 hover:bg-slate-50 ${exportFormat === 'csv' ? 'border-primary bg-primary/5 ring-1 ring-primary' : ''}`}
                                    >
                                        <div className="w-5 h-5 bg-slate-200 rounded flex items-center justify-center text-[10px] font-bold">CSV</div>
                                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">CSV (Brut)</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Colonne Droite : Filtres avec DATE PICKER */}
                        <div className="space-y-4 border-l pl-6">
                            <div className="flex items-center gap-2 mb-2">
                                <CalendarIcon className="w-4 h-4 text-muted-foreground"/>
                                <h3 className="font-semibold text-sm">Filtres optionnels</h3>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Date de début</Label>
                                    {/* UTILISATION DU NOUVEAU COMPOSANT */}
                                    <DatePicker 
                                        date={dateRange.start} 
                                        setDate={(d) => setDateRange({...dateRange, start: d})}
                                        placeholder="Début"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Date de fin</Label>
                                    {/* UTILISATION DU NOUVEAU COMPOSANT */}
                                    <DatePicker 
                                        date={dateRange.end} 
                                        setDate={(d) => setDateRange({...dateRange, end: d})}
                                        placeholder="Fin"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Statut</Label>
                                <Select value={exportStatus} onValueChange={setExportStatus}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {(STATUS_OPTIONS[exportType] || [{value: 'all', label: 'Tous'}]).map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    <div className="pt-4 border-t flex justify-end">
                        <Button size="lg" onClick={handleExport} disabled={loading} className="w-full md:w-auto">
                            {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin"/> : <Download className="w-5 h-5 mr-2"/>}
                            Générer l'export
                        </Button>
                    </div>

                </CardContent>
             </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DataManagementPage;